import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Price from '../components/Price';
import './PremiumSuccess.css';

// Landing del success_url del checkout del marketplace
// (/compra/exito?session_id=cs_...). Confirma el pago contra el backend
// (GET /api/marketplace/confirm, idempotente con el webhook) y saca del
// carrito las piezas que se acaban de pagar.
export default function PurchaseSuccess() {
  const { confirmMarketplaceCheckout } = useAuth();
  const { removeItem } = useCart();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  // 'verifying' | 'paid' | 'processing' | 'error'
  const [status, setStatus] = useState(sessionId ? 'verifying' : 'error');
  const [orders, setOrders] = useState([]);
  const ran = useRef(false);

  useEffect(() => {
    if (!sessionId || ran.current) return;
    ran.current = true; // StrictMode monta dos veces; confirmamos una sola.
    confirmMarketplaceCheckout(sessionId)
      .then(({ paymentStatus, orders: list }) => {
        setOrders(list || []);
        (list || []).forEach((o) => o.publication?._id && removeItem(o.publication._id));
        // 'unpaid' tras completar = método diferido (p. ej. OXXO): el webhook
        // marcará la orden cuando Stripe confirme el pago.
        setStatus(paymentStatus === 'paid' ? 'paid' : 'processing');
      })
      .catch(() => setStatus('error'));
  }, [sessionId, confirmMarketplaceCheckout, removeItem]);

  return (
    <>
      <Helmet>
        <title>Compra — Troly</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <main className="premium-success">
        {status === 'verifying' && (
          <div className="premium-success__card">
            <div className="premium-success__spinner" aria-hidden="true" />
            <h1 className="premium-success__title">Confirmando tu pago…</h1>
            <p className="premium-success__text">Esto tarda solo unos segundos.</p>
          </div>
        )}

        {(status === 'paid' || status === 'processing') && (
          <div className="premium-success__card">
            <div className="premium-success__badge" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 20 20" fill="none">
                <path d="M4 10.5l3.5 3.5L16 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="premium-success__eyebrow">{status === 'paid' ? 'Pago recibido' : 'Pago en proceso'}</span>
            <h1 className="premium-success__title">¡Gracias por tu compra!</h1>
            <p className="premium-success__text">
              {status === 'paid'
                ? 'Avisamos al vendedor para que prepare el envío. Puedes escribirle desde Mensajes.'
                : 'Cuando Stripe confirme tu pago avisaremos al vendedor para que prepare el envío.'}
            </p>
            {orders.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', display: 'grid', gap: 8, textAlign: 'left' }}>
                {orders.map((o) => (
                  <li key={o._id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14 }}>
                    <span>{o.units} × {o.publication?.name || 'Pieza'}</span>
                    <span style={{ fontFamily: 'var(--troly-font-mono)' }}>
                      <Price amount={Number(o.total)} currency={o.currency || 'USD'} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link className="premium-success__cta" to="/">Seguir explorando</Link>
          </div>
        )}

        {status === 'error' && (
          <div className="premium-success__card">
            <h1 className="premium-success__title">No pudimos confirmar el pago</h1>
            <p className="premium-success__text">
              Si Stripe te cobró, la orden se confirmará sola en unos minutos. Si no, vuelve al carrito e inténtalo de nuevo.
            </p>
            <Link className="premium-success__cta premium-success__cta--ghost" to="/cart">Volver al carrito</Link>
          </div>
        )}
      </main>
    </>
  );
}
